import { AuthError } from "@aws-amplify/auth";
import {
  AuthContext,
  type ConfirmResetPassWordInput,
  type ResetPassWordInput,
} from "hooks/authProvider/authProvider";
import { type FC, useCallback, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ResetPassWordPage } from "routes/ResetPassWordPage/ResetPassWordPage";
import { toastError, toastPromise } from "utils/toast-utils";

const MAX_RESEND_COUNT = 3;

const ResetPassWordPagePageContainer: FC = () => {
  const [isSend, setIsSend] = useState(false);
  const [email, setEmail] = useState("");
  const [resendCount, setResendCount] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const { resetPassWord, confirmResetPassWord } = useContext(AuthContext);
  const navigate = useNavigate();
  const handleError = useCallback((error: unknown) => {
    if (error instanceof AuthError) {
      toastError(error.message);
    } else {
      toastError("予期せぬエラーが発生しました");
    }
  }, []);
  const handleResetPassword = useCallback(
    async (input: ResetPassWordInput) => {
      try {
        setEmail(input.email);
        await resetPassWord(input);
        setIsSend(true);
      } catch (error) {
        handleError(error);
      }
    },
    [resetPassWord, handleError],
  );

  const handleResendResetPassword = useCallback(async () => {
    if (isResending) return;
    if (resendCount >= MAX_RESEND_COUNT) {
      return;
    }

    setIsResending(true);
    try {
      await toastPromise(resetPassWord({ email }), {
        pending: "認証コード再送中...",
        success: "認証コードを再送しました",
        error: "認証コードの再送に失敗しました",
      });
      setResendCount(prev => prev + 1);
    } catch (error) {
      handleError(error);
    } finally {
      setIsResending(false);
    }
  }, [email, resetPassWord, resendCount, isResending, handleError]);

  const handleConfirmResetPassword = useCallback(
    async (input: ConfirmResetPassWordInput) => {
      try {
        const confirmInput = { ...input, email };

        await toastPromise(confirmResetPassWord(confirmInput), {
          pending: "パスワード再設定中...",
          success: "パスワードを再設定しました",
          error: "パスワードの再設定に失敗しました",
        });

        navigate("/dashboard");
      } catch (error) {
        handleError(error);
      }
    },
    [confirmResetPassWord, email, navigate, handleError],
  );

  return (
    <ResetPassWordPage
      email={email}
      onResetPassWordPage={handleResetPassword}
      onResendResetPassWord={handleResendResetPassword}
      isSend={isSend}
      onConfirmResetPassWordPage={handleConfirmResetPassword}
      isResending={isResending}
      resendCount={resendCount}
    />
  );
};

export default ResetPassWordPagePageContainer;
